import React, { useEffect, useRef, useState } from "react";
import { Download, FileText, ListChecks, Table2, Box } from "lucide-react";
import clsx from "clsx";

import { API_BASE } from "@/lib/api";

/**
 * Export menu — opens the requested PDF / CSV in a new tab.
 * Browser inherits the auth cookie (httpOnly, sameSite=None) so backend auth resolves.
 */
export default function ExportMenu({ projectId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openReport = (path) => {
    if (!projectId) return;
    window.open(`${API_BASE}/projects/${projectId}/reports/${path}`, "_blank", "noopener");
    setOpen(false);
  };

  const items = [
    { id: "master-pdf", label: "Master Schedule", sub: "PDF · Landscape", icon: FileText, path: "master-schedule.pdf" },
    { id: "master-csv", label: "Master Schedule", sub: "CSV · UTC + local", icon: Table2, path: "master-schedule.csv" },
    { id: "hit-pdf", label: "Hit List", sub: "PDF · Critical + deadline", icon: ListChecks, path: "hit-list.pdf" },
    { id: "hit-csv", label: "Hit List", sub: "CSV", icon: Table2, path: "hit-list.csv" },
    { id: "pull-pdf", label: "Equipment Pull List", sub: "PDF · Sorted by pickup", icon: Box, path: "equipment-pull-list.pdf" },
    { id: "pull-csv", label: "Equipment Pull List", sub: "CSV", icon: Table2, path: "equipment-pull-list.csv" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={clsx("btn-ghost", open && "active")}
        data-testid="export-menu-btn"
        disabled={!projectId}
        title="Export reports"
      >
        <Download className="w-3 h-3" /> <span className="hidden xl:inline">EXPORT</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-64 bg-bg-surface border border-line-strong shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 animate-slide-up"
          data-testid="export-menu"
          role="menu"
        >
          <div className="px-3 py-2 border-b border-line text-2xs mono uppercase tracking-widest text-ink-mute">
            Reports
          </div>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                onClick={() => openReport(it.path)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-bg-elevated border-b border-line-subtle last:border-b-0 transition-colors"
                data-testid={`export-${it.id}`}
                role="menuitem"
              >
                <Icon className="w-3.5 h-3.5 text-amber shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-ink leading-tight">{it.label}</div>
                  <div className="text-2xs mono uppercase tracking-widest text-ink-mute leading-tight">
                    {it.sub}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
